import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Popconfirm, message } from "antd";
import {
  StyledAntdTable,
  StyledButton
} from "common/components/StyledElements";
import { useAuthContext } from "common/contexts/AuthContext";
import APIConstants from "common/constants/APIConstants";
import { api } from "common/utils/APIMethods";
import EditPreBatchModal from "./EditPreBatchModal";

const ProductPreBatchTable = ({ productId }) => {
  const queryClient = useQueryClient();
  const { authConfig } = useAuthContext();
  const { getAllPreBatches, deletePreBatch } = APIConstants;

  const [selectedBatchId, setSelectedBatchId] = useState(null);

  let newGet = getAllPreBatches; 
  newGet+=`/${productId}`

  const { data: batches = [], isLoading } = useQuery({
    queryKey: ["preBatches", productId],
    queryFn: () => api({ url: newGet }, authConfig)
  });

  const normalizedBatches = (
    Array.isArray(batches) ? batches : batches?.json || []
  ).map((batch) => ({
    ...batch,
    id: batch.id ?? batch.batchId,
    createdAt: batch.createdAt ?? batch.creationDateTime
  }));

  const hasProductIdInResponse = normalizedBatches.some(
    (batch) => batch.productId != null
  );

  const filteredBatches = hasProductIdInResponse
    ? normalizedBatches.filter(
        (batch) => String(batch.productId) === String(productId)
      )
    : normalizedBatches;

  const formatDate = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const { mutate: deleteBatchMutate } = useMutation({
    mutationFn: (id) =>
      api(
        {
          url: `${deletePreBatch}/${id}`,
          method: "DELETE"
        },
        authConfig
      ),
    onSuccess: () => {
      message.success("Batch deleted successfully");
      queryClient.invalidateQueries({ queryKey: ["preBatches", productId] });
    }
  });

  const columns = [
    {
      title: "S.No",
      render: (_, __, index) => index + 1
    },
    {
      title: "Quantity To Produce",
      dataIndex: "productQuantity"
    },
    {
      title: "Created Time",
      dataIndex: "createdAt",
      render: (value) => formatDate(value)
    },
    {
      title: "Action",
      render: (_, record) => (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: 8,
            margin: "6px 0"
          }}
        >
          <StyledButton
            type="primary"
            size="small"
            style={{ minWidth: 86 }}
            onClick={() => setSelectedBatchId(record.id)}
          >
            Edit
          </StyledButton>

          <Popconfirm
            title="Are you sure to delete this batch?"
            onConfirm={() => deleteBatchMutate(record.id)}
          >
            <StyledButton type="danger" size="small" style={{ minWidth: 86 }}>
              Delete
            </StyledButton>
          </Popconfirm>
        </div>
      )
    }
  ];

  return (
    <>
      <StyledAntdTable
        rowKey="id"
        columns={columns}
        dataSource={filteredBatches}
        loading={isLoading}
        pagination={false}
      />

      {selectedBatchId && (
        <EditPreBatchModal
          batchId={selectedBatchId}
          onClose={() => setSelectedBatchId(null)}
          onSuccess={() => {
            setSelectedBatchId(null);
            queryClient.invalidateQueries({
              queryKey: ["preBatches", productId]
            });
          }}
        />
      )}
    </>
  );
};

export default ProductPreBatchTable;
